'use client';

/**
 * @fileoverview `DiscoverMapTakeoverLazy` — Client Component wrapper that
 * lazy-loads the connected `DiscoverMapTakeover` organism via `next/dynamic`
 * with `ssr: false`.
 *
 * The Next.js App Router disallows `ssr: false` inside Server Components, so
 * the dynamic import
 * lives in a `'use client'` file the `DiscoverTemplate` (RSC) imports. The
 * takeover (and the Redux/map plumbing it drags in) only ships when
 * `?view=map` is selected; the fallback paints the DS 1.4.0 `GpsPulse`
 * map-loading placeholder (`DiscoverMapLoading`) so a cold map deep-link
 * shows calm "locating" chrome instead of a blank pane.
 */
import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';

import type { CityCode } from '@loci/city-editions';
import type { DiscoverEvent } from '@loci/types';

import type { DiscoverParams } from '@/lib/discover/url-state';

import { DiscoverMapLoading } from './DiscoverMapLoading';

const DiscoverMapTakeover = dynamic(
  () =>
    import('@/components/organisms/DiscoverMapTakeover').then((mod) => ({
      default: mod.DiscoverMapTakeover,
    })),
  {
    ssr: false,
    loading: () => <DiscoverMapLoading />,
  },
);

export interface DiscoverMapTakeoverLazyProps {
  /** Active city — keys the shared discover query subscription. */
  cityCode: CityCode;
  /** Display name surfaced in the takeover bar ("Charlotte"). */
  cityName: string;
  /** SSR-fetched first page of events (drawer + marker seed). */
  events: DiscoverEvent[];
  /** SSR-parsed URL state. */
  params: DiscoverParams;
}

export function DiscoverMapTakeoverLazy({
  cityCode,
  cityName,
  events,
  params,
}: DiscoverMapTakeoverLazyProps): ReactElement {
  return (
    <DiscoverMapTakeover cityCode={cityCode} cityName={cityName} events={events} params={params} />
  );
}
